import { CaretRightLine } from "@/icons";
import { getDataAutomationId } from "@/helpers";
import Link from "@/components/Link";
import { Home } from "@/icons";
import { Link as LinkProps } from "@/types/General";
import cn from "classnames";
import { HTMLAttributes } from "react";

type BreadcrumbsProps = {
  links: Partial<LinkProps>[];
  homeLink?: Partial<LinkProps>;
} & HTMLAttributes<HTMLElement>;

const Breadcrumbs = ({ homeLink, links, ...props }: BreadcrumbsProps) => {
  const showEllipsis = links.length > 3;
  const visibleLinks = links.slice(-3);
  const breadcrumbsItems = showEllipsis ? visibleLinks : links;
  const isLastItem = breadcrumbsItems.length - 1;

  const separator = (
    <CaretRightLine
      width={12}
      height={12}
      fill="var(--color-body-secondary)"
      className="shrink-0"
    />
  );

  if (links.length === 0) {
    return null;
  }

  return (
    <nav
      {...props}
      {...getDataAutomationId("breadcrumbs")}
      className={cn(
        "swdc-container bg-background w-full px-6 py-0.5",
        props?.className,
      )}
      aria-label="Breadcrumb"
    >
      <ol className="flex flex-row items-center overflow-hidden">
        {homeLink && homeLink.href && (
          <li className="flex shrink-0 items-center">
            <Link
              {...homeLink}
              href={homeLink.href}
              className="flex h-6 items-center justify-center"
            >
              <Home
                width={16}
                height={16}
                className="text-link flex items-center"
              />
            </Link>
            {showEllipsis && (
              <span className="text-body-secondary mx-1" aria-hidden>
                ...
              </span>
            )}
            {separator}
          </li>
        )}
        {breadcrumbsItems.map((link, index) => (
          <li
            key={`${link.title}-${index}`}
            className={cn(
              "flex min-w-0 flex-row items-center",
              breadcrumbsItems.length === 1
                ? "flex"
                : "last:hidden nth-last-2:flex sm:last:flex md:nth-last-2:flex",
            )}
          >
            <div className="flex min-w-0 flex-row items-center">
              {index !== 0 && separator}
              {index === isLastItem || !link.href ? (
                <p
                  className="text-link-xs text-body-secondary max-w-65 min-w-0 truncate md:max-w-52.5 lg:max-w-95"
                  aria-current={isLastItem === index ? "page" : undefined}
                >
                  {link.title}
                </p>
              ) : (
                <Link
                  {...link}
                  href={link.href}
                  className="text-link-xs text-link hover:text-link-hover active:text-link-pressed max-w-65 min-w-0 cursor-pointer truncate font-bold hover:underline active:underline md:max-w-52.5 lg:max-w-95"
                >
                  {link.title}
                </Link>
              )}
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
